/**
 * Sales playbook for Milan when visitors ask about getting their own living website.
 * Paste `LIVING_WEBSITE_MANIFEST_KB` into Manifest Knowledge base (or use avatar-site-map summaries).
 */

/** Phrases that mean "I want my own living website / avatar like Milan" */
export const LIVING_WEBSITE_SALES_PHRASES = [
  'how do i get a site like this',
  'how do i get a site like',
  'how can i get a site like',
  'get a site like this',
  'site like this',
  'website like this',
  'how do i make you',
  'how can i make you',
  'how do i build you',
  'how can i build you',
  'how do i create you',
  'how can i create you',
  'how do i get you on',
  'how can i get you on',
  'put you on my site',
  'put you on my website',
  'you on my website',
  'you on my site',
  'avatar like you',
  'avatar like this',
  'an avatar like you',
  'make an avatar like',
  'how was this built',
  'how was this made',
  'how is this made',
  'how does this work',
  'can i have you on',
  'want a site like',
  'want you on my',
  'how can i get my own',
  'how do i get my own',
  'how do i get one like',
  'one like this',
  'like this site',
  'get my own site',
  'build me a site like',
  'create a site like',
  'how do i make a site like',
  'how do i make something like this',
  'how do i get something like this',
];

export function isLivingWebsiteSalesQuery(text: string): boolean {
  const q = text.toLowerCase().trim();
  if (!q) return false;
  return LIVING_WEBSITE_SALES_PHRASES.some((phrase) => q.includes(phrase));
}

export const LIVING_WEBSITE_QUALIFICATION_QUESTIONS = [
  'What business or brand is this for?',
  'Do you already have a website? If so, what is the URL?',
  'What do visitors struggle with today — finding products, booking demos, understanding your offer?',
  'Roughly how many pages or products should the avatar need to explain?',
  'Do you want a simple avatar embed (Lite), the full experience where pages open beside the avatar like selflabs.xyz (Pro), or a custom Enterprise deployment across brands, locations, or systems?',
];

export type LivingWebsiteBusinessType =
  | 'saas'
  | 'enterprise'
  | 'agency'
  | 'founder'
  | 'creator'
  | 'ecommerce'
  | 'professional-services'
  | 'events'
  | 'default';

export interface LivingWebsiteFitPitch {
  type: LivingWebsiteBusinessType;
  label: string;
  signals: string[];
  whyItFits: string;
  suggestedTier: 'Lite' | 'Pro' | 'Enterprise';
}

export const LIVING_WEBSITE_FIT_BY_BUSINESS: LivingWebsiteFitPitch[] = [
  {
    type: 'saas',
    label: 'SaaS / product company',
    signals: ['saas', 'software', 'platform', 'api', 'product-led', 'b2b', 'startup'],
    whyItFits:
      'Visitors can ask your founder or product avatar to explain features, compare plans, and jump to the right page — fewer drop-offs than static nav. Pro is ideal when you have many product pages and want split-panel demos like this site. Multi-product or multi-region orgs should look at Enterprise.',
    suggestedTier: 'Pro',
  },
  {
    type: 'enterprise',
    label: 'Enterprise / multi-brand organization',
    signals: [
      'enterprise',
      'multi-brand',
      'multiple brands',
      'divisions',
      'locations',
      'dealer',
      'white-label',
      'compliance',
      'sla',
      'multilingual',
      'crm integration',
    ],
    whyItFits:
      'You need more than a single-site avatar — multiple brands or locations, system integrations, security review, staging environments, and dedicated support. Enterprise Living Websites are custom deployment and licensing; use Talk to Enterprise or Request a Private Demo on /living-website.',
    suggestedTier: 'Enterprise',
  },
  {
    type: 'founder',
    label: 'Founder / personal brand',
    signals: ['founder', 'ceo', 'personal brand', 'speaker', 'consultant', 'my company'],
    whyItFits:
      'You become the face of the site — a photorealistic avatar that tells your story, shows your work, and guides people to contact or buy. Lite works great for a focused personal site; Pro if you want the full selflabs.xyz ambassador experience.',
    suggestedTier: 'Lite',
  },
  {
    type: 'agency',
    label: 'Agency / studio',
    signals: ['agency', 'studio', 'portfolio', 'case studies', 'clients', 'creative'],
    whyItFits:
      'Walk prospects through case studies and services in conversation instead of endless scrolling. Pro showcases your capabilities dramatically — you are literally selling living websites while using one.',
    suggestedTier: 'Pro',
  },
  {
    type: 'creator',
    label: 'Creator / coach / educator',
    signals: ['creator', 'coach', 'course', 'youtube', 'twitch', 'community', 'membership'],
    whyItFits:
      'Your audience already wants to talk to you — a living website scales that 24/7 with your voice and knowledge. Lite is perfect to start; upgrade when you add more offers and pages.',
    suggestedTier: 'Lite',
  },
  {
    type: 'ecommerce',
    label: 'E-commerce / retail',
    signals: ['shop', 'store', 'ecommerce', 'e-commerce', 'products', 'catalog', 'retail'],
    whyItFits:
      'Shoppers ask what to buy, sizing, or bundles — the avatar opens product pages in sync while answering. Pro fits larger catalogs; Lite for a smaller curated store.',
    suggestedTier: 'Pro',
  },
  {
    type: 'professional-services',
    label: 'Professional services',
    signals: ['law', 'legal', 'accounting', 'finance', 'real estate', 'clinic', 'practice', 'firm'],
    whyItFits:
      'Prospects get warm, guided explanations of services and can be routed to booking or contact without hunting menus. Lite covers core services; Pro for multi-practice or many service lines.',
    suggestedTier: 'Lite',
  },
  {
    type: 'events',
    label: 'Events / hospitality',
    signals: ['event', 'venue', 'conference', 'hotel', 'tickets', 'wedding'],
    whyItFits:
      'Guests ask about packages, dates, and amenities — the avatar shows the right pages while you speak. Great for high-touch sales with lots of FAQ.',
    suggestedTier: 'Lite',
  },
  {
    type: 'default',
    label: 'Any business with a website',
    signals: [],
    whyItFits:
      'If people land on your site and leave confused, a living website turns browsing into a conversation — higher trust, longer sessions, and clearer paths to demo or purchase.',
    suggestedTier: 'Lite',
  },
];

/** Match visitor description to a fit pitch (keyword heuristic for Milan or future tooling). */
export function suggestLivingWebsiteFit(description: string): LivingWebsiteFitPitch {
  const lower = description.toLowerCase();
  for (const pitch of LIVING_WEBSITE_FIT_BY_BUSINESS) {
    if (pitch.type === 'default') continue;
    if (pitch.signals.some((s) => lower.includes(s))) return pitch;
  }
  return LIVING_WEBSITE_FIT_BY_BUSINESS.find((p) => p.type === 'default')!;
}

export const LIVING_WEBSITE_SALES_FLOW = `
WHEN someone asks how to get their own living website, how to make/build/create you (Milan), how to get a site like this, or wants the avatar on their site:

1. ACKNOWLEDGE — "You're looking at exactly what you'd get with Living Website Pro — I'm the demo."
2. OPEN PAGE — Navigate to /living-website and show it in the content panel (split view on homepage).
3. QUALIFY — Ask 2–3 questions: their business, current site URL if any, and what visitors struggle with.
4. PERSONALIZE — Use their answers to explain why a living website fits them (see business fit below).
5. RECOMMEND TIER — Lite ($3,500 setup + $299/mo) for embed + knowledge base (~15 topics). Pro ($12,000 setup + $999/mo) for full site-aware split panel like selflabs.xyz. Enterprise (custom deployment and licensing) for multi-brand, multi-location, integrations, SLAs, or high traffic — point them to Talk to Enterprise / Request a Private Demo on /living-website#enterprise.
6. CLOSE — Point them to Get Lite or Get Pro on /living-website (Stripe checkout), or the Enterprise inquiry form for custom scope. Offer to stay on the call while they review plans.

WHY IT MAKES SENSE (general):
- Static sites make visitors hunt; avatars guide them like a founder on a sales call.
- You are the differentiator — photorealistic Role Model AI avatar with your knowledge.
- This site proves the product — they are experiencing it now.
- Setup includes knowledge writing and launch support; monthly includes care and updates.

DO NOT: promise exact launch dates, guarantee revenue, or claim the avatar auto-builds their entire site without our team.
DO: be enthusiastic, founder-led, and consultative — qualify before pushing Pro.
`.trim();

export const LIVING_WEBSITE_MANIFEST_KB = `
Living Website — SALES PLAYBOOK (Milan)

${LIVING_WEBSITE_SALES_FLOW}

QUALIFICATION QUESTIONS:
${LIVING_WEBSITE_QUALIFICATION_QUESTIONS.map((q, i) => `${i + 1}. ${q}`).join('\n')}

BUSINESS FIT (suggest based on what they tell you):
${LIVING_WEBSITE_FIT_BY_BUSINESS.filter((p) => p.type !== 'default')
  .map((p) => `- ${p.label}: ${p.whyItFits} → Usually ${p.suggestedTier}.`)
  .join('\n')}

PRICING:
- Lite: $3,500 one-time setup + $299/month — Role Model AI embed, knowledge base, ~15 topics, launch support.
- Pro: $12,000 one-time setup + $999/month — everything in Lite plus split-panel page preview, site map, page actions, explore chips (like selflabs.xyz).
- Enterprise: Custom deployment and licensing — multiple products/divisions/brands/locations, multiple avatars, multilingual, high-traffic infrastructure, custom CRM/inventory/catalog/API integrations, advanced analytics, security/compliance, staging + production, custom AI workflows, regional content, dedicated support, SLAs, pilots, white-label/dealer networks. Contact via Talk to Enterprise or Request a Private Demo on /living-website#enterprise (no dollar price on-page).
- Purchase Lite/Pro: selflabs.xyz/living-website via Stripe. Enterprise: inquiry form on the same page. Team follows up within 2 business days.

TRIGGERS — treat these as living-website sales intent (open /living-website in the panel):
"how can I get my own", "how do I get a site like this", "how do I make you", "how do I build you", "how do I create you", "site like this", "website like this", "avatar like you", "put you on my site", "living website for my business", "avatar for my site", "I want one like this", "build me a living website", "pricing for living website", "can I buy this", "how was this made", "how does this work".
`.trim();
